import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Table, Input, Select, Button, Badge, Field, SortControl, sortRows } from "@/lib/ui";

const KINDS = [
  { k: "categories", t: "Categories" },
  { k: "sizes", t: "Sizes" },
  { k: "cities", t: "Cities" },
  { k: "localities", t: "Localities" },
  { k: "stock_locations", t: "Stock Locations" },
];

export default function MasterData() {
  const [kind, setKind] = useState("categories");
  const [rows, setRows] = useState([]);
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState({ name: "", city: "" });
  const [editing, setEditing] = useState(null);
  const [sort, setSort] = useState({ field: "name", dir: "asc" });
  const SORT_FIELDS = [
    { key: "name", label: "Name", type: "str" },
    { key: "status", label: "Status", type: "str" },
  ];

  const load = (k = kind) => api.get(`/admin/master/${k}`).then(({ data }) => setRows(data));
  useEffect(() => { load(); api.get("/admin/master/cities").then(({ data }) => setCities(data)); }, [kind]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    if (kind === "localities" && !form.city) { toast.error("Select a city"); return; }
    try { await api.post(`/admin/master/${kind}`, { name: form.name, city: form.city }); toast.success("Added"); setForm({ name: "", city: "" }); load(); }
    catch (er) { toast.error(apiErr(er)); }
  };
  const toggle = async (r) => {
    await api.put(`/admin/master/${kind}/${r.id}`, { name: r.name, city: r.city, status: r.status === "active" ? "inactive" : "active" });
    load();
  };
  const del = async (r) => { await api.delete(`/admin/master/${kind}/${r.id}`); toast.success("Deleted"); load(); };
  const saveEdit = async () => {
    if (!editing.name.trim()) { toast.error("Name required"); return; }
    if (kind === "localities" && !editing.city) { toast.error("Select a city"); return; }
    try {
      await api.put(`/admin/master/${kind}/${editing.id}`, { name: editing.name, city: editing.city, status: editing.status });
      toast.success("Updated"); setEditing(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <h1 className="font-head text-2xl font-extrabold text-slate-900">Master Data</h1>
      <div className="mt-4 flex flex-wrap gap-2">
        {KINDS.map((x) => (
          <button key={x.k} data-testid={`master-tab-${x.k}`} onClick={() => setKind(x.k)}
            className={`rounded-lg px-4 py-2 text-sm font-semibold ${kind === x.k ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200"}`}>{x.t}</button>
        ))}
      </div>

      <form onSubmit={add} className="mt-4 flex flex-wrap items-end gap-2 rounded-xl border border-slate-200 bg-white p-4">
        <div className="min-w-[200px] flex-1">
          <p className="mb-1 text-xs font-bold uppercase text-slate-500">New {kind.slice(0, -1)}</p>
          <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="master-name" />
        </div>
        {kind === "localities" && (
          <Select className="w-auto" value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} data-testid="master-city">
            <option value="">Select City</option>{cities.map((c) => <option key={c.id}>{c.name}</option>)}
          </Select>
        )}
        <Button type="submit" data-testid="master-add"><i className="fa-solid fa-plus" /> Add</Button>
      </form>

      <div className="mt-4">
        <div className="mb-3"><SortControl fields={SORT_FIELDS} value={sort} onChange={setSort} testid="master-sort" /></div>
        <Table head={kind === "localities" ? ["Name", "City", "Status", "Actions"] : ["Name", "Status", "Actions"]}>
          {sortRows(rows, SORT_FIELDS, sort).map((r) => (
            <tr key={r.id} data-testid={`master-row-${r.id}`}>
              <td className="px-4 py-3 font-semibold text-slate-800">{r.name}</td>
              {kind === "localities" && <td className="px-4 py-3">{r.city}</td>}
              <td className="px-4 py-3"><Badge tone={r.status === "active" ? "green" : "slate"}>{r.status}</Badge></td>
              <td className="px-4 py-3">
                <div className="flex gap-1">
                  <Button variant="outline" onClick={() => setEditing({ ...r })} data-testid={`master-edit-${r.id}`}><i className="fa-solid fa-pen" /></Button>
                  <Button variant="ghost" onClick={() => toggle(r)} data-testid={`master-toggle-${r.id}`}>{r.status === "active" ? "Deactivate" : "Activate"}</Button>
                  <Button variant="ghost" onClick={() => del(r)}><i className="fa-solid fa-trash text-red-500" /></Button>
                </div>
              </td>
            </tr>
          ))}
        </Table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setEditing(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()} data-testid="master-edit-modal">
            <h2 className="font-head text-xl font-extrabold text-slate-900">Edit {kind.slice(0, -1)}</h2>
            <div className="mt-4 space-y-3">
              <Field label="Name"><Input value={editing.name} onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))} data-testid="master-edit-name" /></Field>
              {kind === "localities" && (
                <Field label="City"><Select value={editing.city || ""} onChange={(e) => setEditing((s) => ({ ...s, city: e.target.value }))}>
                  <option value="">Select City</option>{cities.map((c) => <option key={c.id}>{c.name}</option>)}
                </Select></Field>
              )}
              <Field label="Status"><Select value={editing.status} onChange={(e) => setEditing((s) => ({ ...s, status: e.target.value }))}><option value="active">active</option><option value="inactive">inactive</option></Select></Field>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={saveEdit} data-testid="master-edit-save">Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
